import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FirebaseService } from './shared/services/FirebaseService';
import { TenantService } from './platform/services/TenantService';
import { UserManagementService } from './shared/services/UserManagementService';
import { fetchRolePages } from './shared/services/rolePages';
import { canAccessTenant as canAccessTenantForProfile, isPlatformRole } from './shared/services/tenantAccess';
import PlatformDashboard from './platform/components/PlatformDashboard';
import TenantRouter from './tenants/TenantRouter';
import LoadingSpinner from './shared/components/LoadingSpinner';
import ErrorBoundary from './shared/components/ErrorBoundary';
import InvitationAcceptance from './shared/components/InvitationAcceptance';
import InviteTokenPage from './shared/components/InviteTokenPage';
import UserInviteAcceptPage from './apps/routing/components/UserInviteAcceptPage';
import LandingPage from './platform/components/LandingPage';
import RoutingLandingPage from './landing/RoutingLandingPage';
// CHA-279 — new front door (deploy-dark). Inert while FRONT_DOOR_RESKIN=false.
import { FRONT_DOOR_RESKIN } from './frontDoorFlag';
import ChainToLanding from './landing/ChainToLanding';
import SignInScreen from './landing/SignInScreen';

/**
 * Check if current URL is the routing landing page
 */
function isRoutingLandingPage() {
  const path = window.location.pathname;
  const hostname = window.location.hostname;
  // Show routing landing if path is /routing OR if subdomain is routing
  return path === '/routing' || path.startsWith('/routing/') || hostname.startsWith('routing.');
}

/**
 * Parse URL to detect tenant admin invitation token
 * Returns token if URL matches /invite/{token}, null otherwise
 */
function getInviteTokenFromURL() {
  const path = window.location.pathname;
  const match = path.match(/^\/invite\/(.+)$/);
  return match ? match[1] : null;
}

/**
 * Parse URL to detect user invitation token
 * Returns token if URL matches /user-invite/{token}, null otherwise
 */
function getUserInviteTokenFromURL() {
  const path = window.location.pathname;
  const match = path.match(/^\/user-invite\/(.+)$/);
  return match ? match[1] : null;
}

/**
 * Detect invitation links emitted by the user_invitations email sender.
 *
 * Tenant-user invites are accepted after the invitee signs in with the invited
 * email address. The email URL therefore only needs to bypass the public
 * landing page and take the invitee straight to sign-in; the existing
 * getPendingUserInvitation(email) path loads the actual invitation after auth.
 */
function isEmailInvitationSignInURL() {
  if (window.location.pathname !== '/accept-invitation') return false;
  const params = new URLSearchParams(window.location.search);
  return Boolean(params.get('token') || params.get('id'));
}

function isContactPage() {
  return window.location.pathname === '/contact';
}

/**
 * Main Application Component - Actualizer2v1 with Enhanced User Management
 * Handles authentication, routing, permissions, and platform management
 */
export default function App() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [appMode, setAppMode] = useState('platform');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showLandingPage, setShowLandingPage] = useState(true);

  // Check for invitation token in URL
  const [inviteToken, setInviteToken] = useState(() => getInviteTokenFromURL());
  // Check for user invitation token in URL (for tenant users)
  const [userInviteToken, setUserInviteToken] = useState(() => getUserInviteTokenFromURL());
  const [emailInvitationSignIn, setEmailInvitationSignIn] = useState(() => isEmailInvitationSignInURL());
  const [contactPage] = useState(() => isContactPage());

  // CHA-553 (Codex): monotonically-increasing generation, bumped on every
  // loadUserProfile entry. After the async role-page read, a callback commits
  // its profile ONLY if it is still the latest. A UID check is insufficient —
  // the SAME account can sign out and back in (or be role-reassigned) while a
  // read is pending, leaving the UID unchanged while the claims are stale.
  const loadGenRef = useRef(0);
  // CHA-553 (Codex): the uid whose context is currently on screen. Firebase
  // can swap one non-null user for another (e.g. a different account signing
  // in from another tab) with no intervening null callback, so we detect an
  // identity change and clear the previous user's context before the new
  // load, rather than leaving it rendered while the async load runs.
  const currentUidRef = useRef(null);

  /**
   * Enhanced loadUserProfile with comprehensive role detection and permission setup
   */
  const loadUserProfile = useCallback(async (firebaseUser, gen) => {
    // `gen` is claimed by the auth callback (see onAuthStateChanged). Every
    // state commit that follows an `await` below is guarded on it, so a
    // superseded load — newer sign-in, or sign-out — never commits.
    const isStale = () => loadGenRef.current !== gen;
    try {
      console.log('👤 Loading user profile for:', firebaseUser.email);
      console.log('🔍 User UID:', firebaseUser.uid);
      
      // 1. CLAIMS-BASED ROLE DETECTION (CHA-104 / CHA-97)
      // Reads { roleId, tenantId } from Firebase Auth custom claims, minted
      // server-side by mintClaimsOnRoleAssignmentWrite off the roleAssignments
      // collection. This is the ONLY authz path for signed-in users post-PR-C.
      // The Block 2 backstop that read userProfiles directly was removed in
      // CHA-97 — userProfiles is now server-only at the rules layer.
      const tokenResult = await firebaseUser.getIdTokenResult();
      // Superseded during the token read — a newer auth event (sign-in or
      // sign-out) has started; do not commit this profile.
      if (isStale()) {
        console.log('⏭️  Superseded during token read; discarding stale load');
        return;
      }
      const claims = tokenResult.claims || {};

      // Platform-global path: platform_owner or platform_admin. tenantId is
      // intentionally null per the GLOBAL_ROLES contract in
      // functions/platform/roleAssignments.js.
      if (claims.roleId === 'platform_owner' || claims.roleId === 'platform_admin') {
        console.log('👑 Platform role detected via claims:', claims.roleId, firebaseUser.email);
        setUserProfile({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          role: claims.roleId,        // legacy field name for downstream consumers
          roleId: claims.roleId,
          companyId: null,
          tenantId: null,
          mode: 'platform',
          // CHA-104 α-narrow backfill: derive permissions/menuItems from the
          // claims roleId via in-process synchronous lookups so downstream
          // permission gates (PermissionsService.hasPermission,
          // UserManagementService.hasMenuItemAccess) keep working without
          // a Firestore read on signin. Broader-scope role coverage tracked
          // in CHA-107.
          permissions: UserManagementService.getRolePermissions(claims.roleId),
          menuItems: UserManagementService.getRoleMenuItems(claims.roleId),
        });
        setAppMode('platform');
        return;
      }

      // Tenant-scoped path: both tenantId AND roleId must be on the token.
      if (claims.tenantId && claims.roleId) {
        console.log('🏢 Tenant role detected via claims:', claims.roleId, '@', claims.tenantId);
        // CHA-553 (A+B): read the role's page grants so the shell nav can
        // filter on them (Sidebar -> visibleNavGroups). Fails OPEN to
        // undefined — a missing role doc (user/viewer have none) or a read
        // error must NOT lock the user out; visibleNavGroups treats an
        // absent list as allow-by-default. This is a UX filter, not the
        // security boundary (that is parts C/D), so fail-open is correct.
        const pages = await fetchRolePages(claims.tenantId, claims.roleId);
        // Superseded during the role-page read — newer sign-in, sign-out, or
        // same-account reauth. A UID check would miss same-account reauth
        // (UID unchanged); the generation catches every case.
        if (isStale()) {
          console.log('⏭️  Superseded during role-page read; discarding stale profile');
          return;
        }
        setUserProfile({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          role: claims.roleId,        // legacy field name (consumers read .role)
          roleId: claims.roleId,
          companyId: claims.tenantId, // legacy field name (consumers read .companyId for setSelectedTenant)
          tenantId: claims.tenantId,
          mode: 'tenant',
          pages,                      // CHA-553 — role.pages for nav filtering (may be undefined)
          // CHA-104 α-narrow backfill — see platform-path comment above.
          // Tenant roles not in the UserManagementService.rolePermissions
          // table (executive, sales, production, custom roles) fall back to
          // the 'user' entry today; CHA-107 tracks closing that gap.
          permissions: UserManagementService.getRolePermissions(claims.roleId),
          menuItems: UserManagementService.getRoleMenuItems(claims.roleId),
        });
        setAppMode('tenant');
        setSelectedTenant(claims.tenantId);
        return;
      }

      // CHA-97 / PR-C: the Block 2 backstop (legacy userProfiles read +
      // lastLogin write) was removed. With userProfiles locked down at
      // the rules layer (Phase 6) and writes routed through callables,
      // there is no client-side userProfiles read/write path.
      // Stale-token users (claims minted but not yet refreshed in the
      // browser) get the invitation paths below OR the unauthorized
      // fallback. Token-refresh on next call resolves the stale state.

      // 2. CHECK FOR PENDING USER INVITATION
      try {
        console.log('📧 Checking for pending user invitation for:', firebaseUser.email);
        const invitation = await UserManagementService.getPendingUserInvitation(firebaseUser.email);
        if (isStale()) return; // superseded during the invitation read
        if (invitation) {
          console.log('📧 Pending user invitation found:', invitation);
          setUserProfile({ pendingUserInvitation: invitation });
          return;
        }
      } catch (err) {
        console.log('📭 No pending user invitation found');
      }
      
      // 3. CHECK FOR PENDING TENANT INVITATION (backward compatibility)
      try {
        console.log('📧 Checking for pending tenant invitation for:', firebaseUser.email);
        const invitation = await TenantService.getPendingInvitation(firebaseUser.email);
        if (isStale()) return; // superseded during the invitation read
        if (invitation) {
          console.log('📧 Pending tenant invitation found:', invitation);
          setUserProfile({ pendingInvitation: invitation });
          return;
        }
      } catch (err) {
        console.log('📭 No pending tenant invitation found');
      }

      // 4. NO PROFILE OR INVITATION FOUND
      if (isStale()) return; // superseded before the unauthorized fallback
      console.log('🚫 No profile or invitation found - unauthorized user');
      setUserProfile(null);
      setAppMode('unauthorized');
      
    } catch (err) {
      // CHA-553 (Codex P2 / CodeRabbit): an OLDER load can reject after a
      // newer one has already committed. Without this guard the stale
      // rejection would wipe the newer user's profile and drop them to the
      // unauthorized/platform screen until another auth event. Same isStale
      // discipline as the success paths, on the error path.
      if (isStale()) return;
      console.error('❌ Error loading user profile:', err);
      setUserProfile(null);
      setAppMode('platform');
    }
  }, []);

  /**
   * Initialize the application
   */
  const initializeApp = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🚀 Initializing Actualizer2v1 with User Management...');
      
      // Initialize Firebase
      await FirebaseService.initialize();
      
      // Initialize services
      await TenantService.initialize();
      await UserManagementService.initialize();
      
      // Listen for authentication changes
      FirebaseService.onAuthStateChanged(async (user) => {
        console.log('🔐 Auth state changed:', user?.email);
        // CHA-553 (Codex P1 / CodeRabbit): bump the generation on EVERY auth
        // event — including sign-out (null). loadUserProfile's async steps
        // check this before committing, so a read still pending from a prior
        // user (or a prior session of the same account) can never restore a
        // stale profile after sign-out or a newer sign-in. The bump must live
        // here, not inside loadUserProfile, so the null branch invalidates
        // pending loads too.
        const gen = ++loadGenRef.current;
        const prevUid = currentUidRef.current;
        currentUidRef.current = user?.uid ?? null;
        setUser(user);
        if (user) {
          setShowLandingPage(false);
          // CHA-553 (Codex P1): if a DIFFERENT account has arrived, clear the
          // previous user's committed context immediately and show loading, so
          // TenantRouter never renders A's tenant for B during B's async load.
          // A same-uid event (token refresh) skips this — no clear, no flicker.
          if (user.uid !== prevUid) {
            setUserProfile(null);
            setAppMode('platform');
            setSelectedTenant(null);
            setLoading(true);
          }
          await loadUserProfile(user, gen);
        } else {
          setUserProfile(null);
          setAppMode('platform');
          setSelectedTenant(null);
        }
        // Only the latest auth event owns the loading flag.
        if (loadGenRef.current === gen) setLoading(false);
      });
      
      console.log('✅ Actualizer2v1 initialized successfully');
      
    } catch (err) {
      console.error('❌ Initialization failed:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [loadUserProfile]);

  /**
   * Handle user invitation acceptance
   */
  const handleUserInvitationAccepted = async (newUserProfile) => {
    console.log('✅ User invitation accepted, updating profile');
    setUserProfile(newUserProfile);
    setAppMode(newUserProfile.mode);
    setSelectedTenant(newUserProfile.companyId);
  };

  /**
   * Handle tenant invitation acceptance (backward compatibility)
   */
  const handleTenantInvitationAccepted = async (newUserProfile) => {
    console.log('✅ Tenant invitation accepted, updating profile');
    setUserProfile(newUserProfile);
    setAppMode(newUserProfile.mode);
    setSelectedTenant(newUserProfile.companyId);
  };

  /**
   * Handle sign in from landing page
   */
  const handleSignInFromLanding = () => {
    console.log('🔗 Navigating from landing page to login');
    setShowLandingPage(false);
  };

  const handleInvitationBackToLanding = () => {
    setEmailInvitationSignIn(false);
    setShowLandingPage(true);
  };

  /**
   * Check if user can access platform admin features
   */
  const canAccessPlatform = () => {
    return userProfile?.role === 'platform_owner';
  };

  /**
   * Check if user can access tenant features
   * ✅ FIXED: Platform owner can access any tenant
   */
  const canAccessTenant = () => {
    // CHA-552: the predicate lives in shared/services/tenantAccess.js so it
    // can be tested without App's dependency graph. Entry now depends on
    // holding a tenant-scoped role rather than on the role's NAME — the old
    // four-name allowlist rejected every custom role and three of the five
    // seeded built-in roles. Page access (PermissionService) and data scope
    // (userScope) are unchanged and still apply after entry.
    if (isPlatformRole(userProfile?.role)) {
      console.log('✅ Platform owner can access tenant:', selectedTenant);
      return true;
    }

    const hasAccess = canAccessTenantForProfile(userProfile);
    console.log('🔍 Tenant access check:', {
      hasCompanyId: !!userProfile?.companyId,
      role: userProfile?.role,
      hasAccess
    });
    return hasAccess;
  };

  // Initialize on mount
  useEffect(() => {
    initializeApp();
  }, [initializeApp]);

  // ✅ DEBUG: Add logging to track app state changes
  useEffect(() => {
    console.log('🔍 App State Debug:', {
      appMode,
      selectedTenant,
      canAccessPlatform: canAccessPlatform(),
      canAccessTenant: canAccessTenant(),
      userRole: userProfile?.role
    });
  }, [appMode, selectedTenant, userProfile]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-blue-900 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <h2 className="text-xl font-semibold text-white mb-2">Welcome to Supply ChainTo A.I</h2>
          <p className="text-gray-300">Initializing your workspace...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="text-red-500 text-4xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Connection Error</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Routing product landing page (/routing or routing.* subdomain)
  if (isRoutingLandingPage()) {
    return (
      <RoutingLandingPage
        onSignIn={() => {
          // Navigate to main app for sign in
          window.location.href = '/';
        }}
      />
    );
  }

  // Handle invitation token URL (/invite/{token}) - for tenant admin invitations
  if (inviteToken) {
    return (
      <InviteTokenPage
        token={inviteToken}
        onComplete={(newUserProfile) => {
          console.log('✅ Invitation accepted via token, redirecting...');
          setInviteToken(null); // Clear token
          setUserProfile(newUserProfile);
          setAppMode('tenant');
          setSelectedTenant(newUserProfile.companyId);
          // Reload to trigger normal auth flow
          window.location.href = '/';
        }}
      />
    );
  }

  // Handle user invitation token URL (/user-invite/{token}) - for tenant user invitations
  if (userInviteToken) {
    return (
      <UserInviteAcceptPage
        token={userInviteToken}
        onComplete={(newUserProfile) => {
          console.log('✅ User invitation accepted, redirecting...');
          setUserInviteToken(null); // Clear token
          setUserProfile(newUserProfile);
          setAppMode('tenant');
          setSelectedTenant(newUserProfile.companyId);
          // Reload to trigger normal auth flow
          window.location.href = '/';
        }}
      />
    );
  }

  // Email invitation links use /accept-invitation?token=...&id=...
  // (functions/platform/emailInvitations.js). Tenant-user acceptance is
  // email-based after sign-in, so this route must skip the public landing and
  // present auth directly; otherwise the invite button appears to do nothing.
  if (!user && emailInvitationSignIn) {
    return FRONT_DOOR_RESKIN ? (
      <SignInScreen
        onLogin={initializeApp}
        onBackToLanding={handleInvitationBackToLanding}
      />
    ) : (
      <LoginScreen
        onLogin={initializeApp}
        onBackToLanding={handleInvitationBackToLanding}
      />
    );
  }

  // Show landing page if not authenticated and landing is enabled
  // CHA-279: FRONT_DOOR_RESKIN=false keeps the existing LandingPage byte-identical.
  if (!user && showLandingPage) {
    if (contactPage) {
      return (
        <LandingPage
          onSignIn={handleSignInFromLanding}
          initialContactOpen
        />
      );
    }

    return FRONT_DOOR_RESKIN ? (
      <ChainToLanding onSignIn={handleSignInFromLanding} />
    ) : (
      <LandingPage
        onSignIn={handleSignInFromLanding}
        initialContactOpen={false}
      />
    );
  }

  // Not authenticated - show login
  // CHA-279: FRONT_DOOR_RESKIN=false keeps the existing LoginScreen byte-identical.
  if (!user) {
    return FRONT_DOOR_RESKIN ? (
      <SignInScreen
        onLogin={initializeApp}
        onBackToLanding={() => setShowLandingPage(true)}
      />
    ) : (
      <LoginScreen
        onLogin={initializeApp}
        onBackToLanding={() => setShowLandingPage(true)}
      />
    );
  }

  // User has pending user invitation
  if (userProfile?.pendingUserInvitation) {
    return (
      <UserInvitationAcceptance
        user={user}
        invitation={userProfile.pendingUserInvitation}
        onAccepted={handleUserInvitationAccepted}
      />
    );
  }

  // User has pending tenant invitation (backward compatibility)
  if (userProfile?.pendingInvitation) {
    return (
      <InvitationAcceptance
        user={user}
        invitation={userProfile.pendingInvitation}
        onAccepted={handleTenantInvitationAccepted}
      />
    );
  }

  // Unauthorized user - no invitation found
  if (appMode === 'unauthorized' || (!userProfile && user)) {
    return (
      <UnauthorizedAccess 
        user={user}
        onSignOut={() => FirebaseService.signOut()}
      />
    );
  }

  return (
    <ErrorBoundary fallbackMessage="Something went wrong with the Supply ChainTo A.I platform">
      {/* Platform Admin View - Only for Platform Owners */}
      {appMode === 'platform' && canAccessPlatform() && (
        <div className="min-h-screen bg-gray-50">
          {/* Platform Header */}
          <header className="bg-white shadow-sm border-b border-gray-200">
            <div className="max-w-[1600px] mx-auto px-8 py-10 space-y-10">
              <div className="flex justify-between items-center py-4">
                <div className="flex items-center gap-4">
                  <img 
                    src="/tenants/inmocean/config/ChainTo.png" 
                    alt="Supply ChainTo A.I Logo" 
                    className="w-8 h-8 object-contain"
                  />
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">ChainTo</h1>
                    <p className="text-sm text-gray-600">Multi-tenant Business Intelligence Platform</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{userProfile?.name || user.email}</p>
                    <p className="text-xs text-blue-600">
                      {UserManagementService.formatRoleName(userProfile?.role)}
                    </p>
                  </div>
                  <button
                    onClick={() => FirebaseService.signOut()}
                    className="bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          </header>

          {/* Platform Content */}
          <main className="max-w-[1600px] mx-auto px-8 py-10 space-y-10">
            <PlatformDashboard 
              user={user} 
              userProfile={userProfile}
              onTenantSelect={(tenantId) => {
                console.log('🎯 Platform onTenantSelect called with:', tenantId);
                setSelectedTenant(tenantId);
                setAppMode('tenant');
                console.log('🔄 Switching to tenant mode for:', tenantId);
              }}
            />
          </main>
        </div>
      )}

      {/* Tenant View - For All Tenant Users AND Platform Owner */}
      {appMode === 'tenant' && selectedTenant && canAccessTenant() && (
        <TenantRouter 
          companyId={selectedTenant}
          userProfile={userProfile}
          onReturnToPlatform={() => {
            console.log('🔄 Returning to platform from tenant:', selectedTenant);
            if (canAccessPlatform()) {
              setAppMode('platform');
              setSelectedTenant(null);
            }
          }}
        />
      )}

      {/* ✅ DEBUG: Show current state if neither view is active */}
      {appMode === 'tenant' && selectedTenant && !canAccessTenant() && (
        <div className="min-h-screen bg-red-50 flex items-center justify-center">
          <div className="max-w-md w-full mx-4">
            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
              <div className="text-red-500 text-4xl mb-4">🔍</div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Debug: Tenant Access Blocked</h2>
              <div className="text-sm text-gray-600 space-y-2">
                <p><strong>App Mode:</strong> {appMode}</p>
                <p><strong>Selected Tenant:</strong> {selectedTenant}</p>
                <p><strong>User Role:</strong> {userProfile?.role}</p>
                <p><strong>Company ID:</strong> {userProfile?.companyId || 'None'}</p>
                <p><strong>Can Access Platform:</strong> {canAccessPlatform() ? 'Yes' : 'No'}</p>
                <p><strong>Can Access Tenant:</strong> {canAccessTenant() ? 'Yes' : 'No'}</p>
              </div>
              <button
                onClick={() => {
                  setAppMode('platform');
                  setSelectedTenant(null);
                }}
                className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Return to Platform
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fallback for invalid states */}
      {(!canAccessPlatform() && !canAccessTenant()) && appMode !== 'tenant' && (
        <UnauthorizedAccess 
          user={user}
          userProfile={userProfile}
          onSignOut={() => FirebaseService.signOut()}
        />
      )}
    </ErrorBoundary>
  );
}

/**
 * User Invitation Acceptance Component
 */
function UserInvitationAcceptance({ user, invitation, onAccepted }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleAcceptInvitation = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('✅ Accepting user invitation...');
      
      const userProfile = await UserManagementService.acceptUserInvitation(invitation.id, {
        uid: user.uid,
        email: user.email,
        name: user.displayName || user.email.split('@')[0]
      });

      onAccepted(userProfile);

    } catch (error) {
      console.error('❌ Failed to accept invitation:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 to-purple-900 flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="text-center mb-6">
            <div className="text-4xl mb-4">🎉</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">You're Invited!</h1>
            <p className="text-gray-600">You've been invited to join:</p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h2 className="font-semibold text-blue-900 mb-1">
              {invitation.company?.name || 'Company'}
            </h2>
            <p className="text-sm text-blue-700 mb-2">
              Role: {UserManagementService.formatRoleName(invitation.role)}
            </p>
            <p className="text-xs text-blue-600">
              Invited by: {invitation.invitedBy}
            </p>
            {invitation.message && (
              <p className="text-sm text-blue-800 mt-2 italic">"{invitation.message}"</p>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          <button
            onClick={handleAcceptInvitation}
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-4"
          >
            {loading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Accepting Invitation...
              </div>
            ) : (
              'Accept Invitation'
            )}
          </button>

          <button
            onClick={() => FirebaseService.signOut()}
            className="w-full border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Unauthorized Access Component
 */
function UnauthorizedAccess({ user, userProfile, onSignOut }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-red-500 text-4xl mb-4">🚫</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Restricted</h2>
          <p className="text-gray-600 mb-4">
            Your account ({user.email}) doesn't have access to this platform.
          </p>
          
          {userProfile && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <p className="text-yellow-800 text-sm">
                Your role: {UserManagementService.formatRoleName(userProfile.role)}
              </p>
              {userProfile.companyId && (
                <p className="text-yellow-700 text-xs">
                  Company: {userProfile.companyId}
                </p>
              )}
            </div>
          )}
          
          <p className="text-sm text-gray-500 mb-6">
            Please contact your administrator if you believe this is an error, or wait for an invitation to be sent to your email.
          </p>
          
          <button
            onClick={onSignOut}
            className="w-full bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Enhanced Login Screen Component with Email Authentication
 */
function LoginScreen({ onLogin, onBackToLanding }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [authMethod, setAuthMethod] = useState('google');
  
  // Email auth states
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      setError(null);
      
      await FirebaseService.signInWithGoogle();
      onLogin();
      
    } catch (error) {
      console.error('❌ Login failed:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    
    try {
      if (!email.trim()) {
        throw new Error('Email is required');
      }
      
      if (!password.trim()) {
        throw new Error('Password is required');
      }

      if (isSignUp) {
        if (password.length < 6) {
          throw new Error('Password must be at least 6 characters');
        }
        
        if (password !== confirmPassword) {
          throw new Error('Passwords do not match');
        }
      }

      if (isSignUp) {
        console.log('👤 Creating account for:', email);
        await FirebaseService.createUserWithEmail(email.trim(), password);
      } else {
        console.log('🔑 Signing in with email:', email);
        await FirebaseService.signInWithEmail(email.trim(), password);
      }
      
      onLogin();
      
    } catch (error) {
      console.error('❌ Email auth failed:', error);
      
      let friendlyMessage = error.message;
      
      if (error.code === 'auth/user-not-found') {
        friendlyMessage = 'No account found with this email address';
      } else if (error.code === 'auth/wrong-password') {
        friendlyMessage = 'Incorrect password';
      } else if (error.code === 'auth/email-already-in-use') {
        friendlyMessage = 'An account with this email already exists';
      } else if (error.code === 'auth/invalid-email') {
        friendlyMessage = 'Please enter a valid email address';
      } else if (error.code === 'auth/weak-password') {
        friendlyMessage = 'Password should be at least 6 characters';
      } else if (error.code === 'auth/too-many-requests') {
        friendlyMessage = 'Too many failed attempts. Please try again later';
      }
      
      setError(friendlyMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    
    try {
      if (!email.trim()) {
        throw new Error('Please enter your email address');
      }
      
      await FirebaseService.sendPasswordReset(email.trim());
      setResetSent(true);
      console.log('📧 Password reset email sent to:', email);
    } catch (error) {
      console.error('❌ Password reset error:', error);
      
      let friendlyMessage = error.message;
      if (error.code === 'auth/user-not-found') {
        friendlyMessage = 'No account found with this email address';
      } else if (error.code === 'auth/invalid-email') {
        friendlyMessage = 'Please enter a valid email address';
      }
      
      setError(friendlyMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-blue-900 flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-lg shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <img 
                src="/tenants/inmocean/config/ChainTo.png" 
                alt="Supply ChainTo A.I Logo" 
                className="w-16 h-16 object-contain"
              />
            </div>
            <h1 className="text-3xl font-bold mb-2 text-gray-900">ChainTo</h1>
            <p className="text-gray-600">Access your platform</p>
          </div>

          {/* Auth Method Selection */}
          {authMethod === 'google' && (
            <div>
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}
              
              {/* Google Sign In Button */}
              <button
                onClick={handleGoogleLogin}
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mb-4"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Signing in...
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </>
                )}
              </button>

              {/* Divider */}
              <div className="relative flex items-center justify-center my-6">
                <div className="border-t border-gray-300 w-full"></div>
                <span className="bg-white px-3 text-sm text-gray-500">or</span>
                <div className="border-t border-gray-300 w-full"></div>
              </div>

              {/* Email Option Button */}
              <button
                onClick={() => setAuthMethod('email')}
                className="w-full border-2 border-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 mb-4"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Sign in with Email
              </button>

              {/* Back to Landing */}
              <button
                onClick={onBackToLanding}
                className="w-full text-gray-500 hover:text-gray-700 transition-colors text-sm"
              >
                ← Back to Home
              </button>
            </div>
          )}

          {/* Email Authentication Form */}
          {authMethod === 'email' && (
            <div>
              {/* Tab Navigation */}
              <div className="flex mb-6 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => {
                    setIsSignUp(false);
                    setError(null);
                    setPassword('');
                    setConfirmPassword('');
                  }}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    !isSignUp 
                      ? 'bg-white text-blue-600 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Sign In
                </button>
                <button
                  onClick={() => {
                    setIsSignUp(true);
                    setError(null);
                    setPassword('');
                    setConfirmPassword('');
                  }}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    isSignUp 
                      ? 'bg-white text-blue-600 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Sign Up
                </button>
              </div>

              {/* Email Form */}
              <form onSubmit={handleEmailAuth} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your email"
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                      placeholder={isSignUp ? 'Create a password (min 6 characters)' : 'Enter your password'}
                      disabled={loading}
                      autoComplete={isSignUp ? 'new-password' : 'current-password'}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>

                {isSignUp && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirm Password
                    </label>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Confirm your password"
                      disabled={loading}
                      autoComplete="new-password"
                    />
                  </div>
                )}

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-red-800 text-sm">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      {isSignUp ? 'Creating Account...' : 'Signing In...'}
                    </div>
                  ) : (
                    isSignUp ? 'Create Account' : 'Sign In'
                  )}
                </button>
              </form>

              {/* Forgot Password Link */}
              {!isSignUp && (
                <div className="text-center mt-4">
                  <button
                    onClick={() => setAuthMethod('reset')}
                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    Forgot your password?
                  </button>
                </div>
              )}

              {/* Back to Google */}
              <div className="text-center mt-6">
                <button
                  onClick={() => {
                    setAuthMethod('google');
                    setError(null);
                    setEmail('');
                    setPassword('');
                    setConfirmPassword('');
                  }}
                  className="text-sm text-gray-600 hover:text-gray-800"
                >
                  ← Back to Google Sign In
                </button>
              </div>
            </div>
          )}

          {/* Password Reset */}
          {authMethod === 'reset' && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4 text-center">
                Reset Password
              </h2>
              <p className="text-gray-600 text-center mb-6">
                Enter your email to receive reset instructions
              </p>

              {resetSent ? (
                <div className="text-center">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <h3 className="text-green-800 font-medium mb-2">Check Your Email!</h3>
                    <p className="text-green-700 text-sm">
                      We've sent password reset instructions to <strong>{email}</strong>
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setAuthMethod('email');
                      setResetSent(false);
                      setEmail('');
                      setError(null);
                    }}
                    className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Back to Sign In
                  </button>
                </div>
              ) : (
                <form onSubmit={handlePasswordReset} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter your email"
                      disabled={loading}
                    />
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-red-800 text-sm">{error}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Sending...
                      </div>
                    ) : (
                      'Send Reset Email'
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setAuthMethod('email');
                      setError(null);
                      setEmail('');
                    }}
                    className="w-full border border-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Back to Sign In
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
